import React, { useCallback, useEffect, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import "react-pdf/dist/esm/Page/TextLayer.css";
import {
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  DocumentTextIcon,
  UserIcon,
  EnvelopeIcon,
  ArrowRightIcon,
  ArrowDownTrayIcon,
  CheckIcon,
  XMarkIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  ArrowPathIcon,
  CalendarIcon,
  ChevronRightIcon,
} from "@heroicons/react/24/outline";
import { format } from "date-fns";

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

import {
  getOfferedCandidateDetails,
  approveStep1GeneralDetails,
  rejectStep1GeneralDetails,
  approveStep2SignedContract,
  rejectStep2SignedContract,
  approveStep3WelcomePack,
  rejectStep3WelcomePack,
  downloadSignedContractByCandidate,
  getGeneratedContractPdf,
  searchOfferedCandidates,
  getAssignedBuddies,
  regenerateContract,
  reissueContract,
  markOnboardingComplete,
  initiateOnboarding,
  getContractTemplates,
} from "../../api/offeredcandidate.api";
import OnboardingBuddyModal from "./OnBoardingBuddyModal";
import CalendarInviteModal from "./CalendarInviteModal";
import useUserData from "../../hooks/useUserData";
import useGraphApiToken from "../../hooks/useGraphApiToken";
import useApiToken from "../../hooks/useApiToken";
import { useNotification } from "../../context/NotificationContext";

/* ─────────────────────────── Types ─────────────────────────── */

type CandidateSubmission = {
  id: string;
  candidateId: string;
  candidateName: string;
  candidateEmail: string;
  roleId: string;
  roleTitle: string;
  currentStatus: "offered" | "initiated" | "pending" | "approved" | "rejected";
  currentStep: "offered" | "step1" | "step2" | "step3" | "completed";
  submittedAt: Date;
  updatedAt: Date;
  data?: any;
};

type BuddyAssignment = {
  id: string;
  name: string | null;
  email: string | null;
};

type CandidateDetails = {
  candidateId: string;
  offerId: string;
  email: string;
  name: string;
  currentStatus: string;
  createdAt: string;
  roleId: string;
  roleTitle: string;
  msEmail?: string;
  // Part-time / internship period configuration (null when the recruiter did not set a date)
  internshipPeriodStartDate?: string | null;
  isInterestedInPartTime?: boolean | null;
  partTimeStartDate?: string | null;
  generalDetails: any;
  signedContract: any;
  welcomePackDetails: any;
};

/* ─────────────────────────── Helpers ─────────────────────────── */

const parseUtcDate = (value: Date | string) => {
  if (value instanceof Date) return value;
  const hasTimezone = /(?:z|[+-]\d{2}:?\d{2})$/i.test(value);
  return new Date(hasTimezone ? value : `${value}Z`);
};

function debounce<T extends (...args: any[]) => any>(func: T, wait: number): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout;
  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
}

/* ─────────────────────────── Sub-components ─────────────────────────── */

const StatusBadge = ({ status }: { status: string }) => {
  const map: Record<string, { bg: string; text: string; label: string; icon: React.ReactNode }> = {
    offered: { bg: "bg-indigo-100 dark:bg-indigo-900", text: "text-indigo-800 dark:text-indigo-200", label: "Offered", icon: <ClockIcon className="h-3 w-3" /> },
    pending: { bg: "bg-yellow-100 dark:bg-yellow-900", text: "text-yellow-800 dark:text-yellow-200", label: "Pending", icon: <ClockIcon className="h-3 w-3" /> },
    approved: { bg: "bg-green-100 dark:bg-green-900", text: "text-green-800 dark:text-green-200", label: "Approved", icon: <CheckCircleIcon className="h-3 w-3" /> },
    rejected: { bg: "bg-red-100 dark:bg-red-900", text: "text-red-800 dark:text-red-200", label: "Rejected", icon: <XCircleIcon className="h-3 w-3" /> },
    initiated: { bg: "bg-blue-100 dark:bg-blue-900", text: "text-blue-800 dark:text-blue-200", label: "Initiated", icon: <ClockIcon className="h-3 w-3" /> },
    completed: { bg: "bg-teal-100 dark:bg-teal-900", text: "text-teal-800 dark:text-teal-200", label: "Completed", icon: <CheckCircleIcon className="h-3 w-3" /> },
  };
  const s = map[status] ?? map.pending;
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${s.bg} ${s.text}`}>
      {s.icon}{s.label}
    </span>
  );
};

const FieldCard = ({ label, value, wide = false }: { label: string; value: React.ReactNode; wide?: boolean }) => (
  <div className={`${wide ? "col-span-2" : ""} p-3 bg-gray-50 dark:bg-darkbglight rounded-lg border border-transparent dark:border-gray-700`}>
    <p className="text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-1">{label}</p>
    <p className="text-sm font-medium text-gray-900 dark:text-gray-200 leading-snug">{value || "—"}</p>
  </div>
);

const StepDivider = ({ label }: { label: string }) => (
  <div className="flex items-center gap-3 my-2">
    <div className="h-px flex-1 bg-gray-200 dark:bg-gray-700" />
    <span className="text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-widest whitespace-nowrap">{label}</span>
    <div className="h-px flex-1 bg-gray-200 dark:bg-gray-700" />
  </div>
);

/* ─────────────────────────── Main Component ─────────────────────────── */

export const AdminDashboard = () => {
  const { localAccountId, username } = useUserData();
  const token = useApiToken();
  const graphToken = useGraphApiToken();
  const { success, error } = useNotification();

  const [adminCredentials, setAdminCredentials] = useState<{ id?: string; name?: string }>({});
  const [regenerating, setRegenerating] = useState(false);
  const [regenerateResult, setRegenerateResult] = useState<{ ok: boolean; msg: string } | null>(null);
  const [selectedSubmission, setSelectedSubmission] = useState<CandidateSubmission | null>(null);
  const [candidateDetails, setCandidateDetails] = useState<CandidateDetails | null>(null);
  const [submissions, setSubmissions] = useState<CandidateSubmission[]>([]);
  const [rejectReason, setRejectReason] = useState("");
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectStep, setRejectStep] = useState<"step1" | "step2" | "step3" | null>(null);
  const [initiateCandidate, setInitiateCandidate] = useState<CandidateSubmission | null>(null);
  // Optional internship period start date captured while initiating onboarding ("yyyy-MM-dd")
  const [internshipStartDate, setInternshipStartDate] = useState("");
  const [showApproveConfirm, setShowApproveConfirm] = useState(false);
  const [approveStep, setApproveStep] = useState<"step1" | "step2" | "step3" | null>(null);
  const [filterStatus, setFilterStatus] = useState<"all" | "offered" | "initiated" | "pending" | "approved" | "rejected">("all");
  const [filterStep, setFilterStep] = useState<"all" | "offered" | "step1" | "step2" | "step3" | "completed">("all");
  const [isLoading, setIsLoading] = useState(false);
  const [isStepDetailsLoading, setIsStepDetailsLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [showBuddyModal, setShowBuddyModal] = useState(false);
  const [showCalendarModal, setShowCalendarModal] = useState(false);
  const [isRescheduling, setIsRescheduling] = useState(false);
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [pdfPageCount, setPdfPageCount] = useState(0);
  const [pdfModalTitle, setPdfModalTitle] = useState("Contract Preview");
  const [previewLoading, setPreviewLoading] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState<CandidateSubmission | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [preAssignedBuddies, setPreAssignedBuddies] = useState<BuddyAssignment[]>([]);
  const [meetingDetails, setMeetingDetails] = useState<{
    meetingLink: string;
    meetingTitle: string;
    meetingStartTime: string;
    meetingEndTime: string;
  } | null>(null);
  const [showMsEmailModal, setShowMsEmailModal] = useState(false);
  const [msEmail, setMsEmail] = useState("");
  const [isMarkingComplete, setIsMarkingComplete] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [contractTemplates, setContractTemplates] = useState<any[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<string>("");
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  // Re-issue: re-generate the contract (optionally from a different template) and send it again
  // to a candidate whose contract signing was not approved.
  const [showReissueModal, setShowReissueModal] = useState(false);
  const [reissueTemplate, setReissueTemplate] = useState<string>("");
  const [reissueReason, setReissueReason] = useState("");
  const [notifyCandidateOnReissue, setNotifyCandidateOnReissue] = useState(true);
  const [isReissuing, setIsReissuing] = useState(false);
  const [activeTab, setActiveTab] = useState<"step1" | "step2" | "step3">("step1");

  const debouncedSearch = useCallback(
    debounce((query: string, status: string, step: string, page: number) => {
      fetchSubmissions(query, status, step, page);
    }, 500),
    [],
  );

  useEffect(() => {
    if (localAccountId && username) setAdminCredentials({ id: localAccountId, name: username });
  }, [localAccountId, username]);

  useEffect(() => {
    setCurrentPage(1);
    if (token) debouncedSearch(searchQuery, filterStatus, filterStep, 1);
  }, [searchQuery, filterStatus, filterStep, token]);

  useEffect(() => {
    if (currentPage > 1 && token) fetchSubmissions(searchQuery, filterStatus, filterStep, currentPage);
  }, [currentPage, token]);

  useEffect(() => {
    if (token) fetchSubmissions(searchQuery, filterStatus, filterStep, currentPage);
  }, [token]);

  useEffect(() => {
    if (showTemplateModal) loadContractTemplates();
  }, [showTemplateModal]);

  useEffect(() => {
    // Pre-select the role's default template so re-generating the same contract is one click;
    // the recruiter can switch to a different one in the modal.
    if (showReissueModal) loadContractTemplates().then((fileName) => setReissueTemplate((prev) => prev || fileName));
  }, [showReissueModal]);

  useEffect(() => {
    fetchAssignedBuddies();
  }, [selectedSubmission]);

  const fetchSubmissions = async (search: string, status: string, step: string, page: number) => {
    try {
      setIsLoading(true);
      const response = await searchOfferedCandidates({ search, status: status === "all" ? undefined : status, step: step === "all" ? undefined : step, page, pageSize: 20 }, token!);
      setSubmissions(response.data);
      setTotalPages(response.pagination.totalPages);
      setTotalCount(response.pagination.totalCount);
    } catch (err) {
      console.error("Error fetching offered candidates", err);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAssignedBuddies = async () => {
    if (!selectedSubmission?.id) return;
    try {
      const response = await getAssignedBuddies(selectedSubmission.id, token!);
      setPreAssignedBuddies(Array.isArray(response?.data) ? response.data : []);
      setMeetingDetails(response?.meetingDetails || null);
    } catch {
      setPreAssignedBuddies([]);
      setMeetingDetails(null);
    }
  };

  const fetchCandidateDetails = async (candidateId: string) => {
    try {
      setIsStepDetailsLoading(true);
      setCandidateDetails(null);
      const details = await getOfferedCandidateDetails(candidateId, token!);
      setCandidateDetails(details);
    } catch (err) {
      console.error("Error fetching candidate details:", err);
    } finally {
      setIsStepDetailsLoading(false);
    }
  };

  const handleReviewButtonClick = async (submission: CandidateSubmission) => {
    setSelectedSubmission(submission);
    setActiveTab("step1");
    await fetchCandidateDetails(submission.candidateId);
  };

  const handleInitiateOnboarding = async () => {
    if (actionLoading) return;
    if (!initiateCandidate || !token) return;
    try {
      setActionLoading(true);
      await initiateOnboarding(
        initiateCandidate.id,
        initiateCandidate.candidateId,
        token,
        internshipStartDate || null,
      );
      await fetchSubmissions(searchQuery, filterStatus, filterStep, currentPage);
      success(
        "Onboarding Initiated",
        internshipStartDate
          ? "The role offer and part-time option emails have been sent. The candidate's commencement date is fixed to the internship period start date."
          : "The role offer email has been sent and the candidate can now complete Step 1.",
      );
      setInitiateCandidate(null);
      setInternshipStartDate("");
    } catch (err) {
      error("Initiation Failed", `${err instanceof Error ? err.message : "Unknown error"}`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleApprove = async () => {
    if (actionLoading) return;
    if (!selectedSubmission || !approveStep) return;

    if (approveStep === "step1") {
      setShowApproveConfirm(false);
      setShowTemplateModal(true);
      return;
    }

    try {
      setActionLoading(true);
      if (approveStep === "step2") {
        await approveStep2SignedContract(candidateDetails?.signedContract.id, adminCredentials.id ?? "", adminCredentials.name ?? "", token!);
      } else if (approveStep === "step3") {
        await approveStep3WelcomePack(candidateDetails?.welcomePackDetails.id, adminCredentials.id ?? "", adminCredentials.name ?? "", token!);
      }
      await fetchSubmissions(searchQuery, filterStatus, filterStep, currentPage);
      await fetchCandidateDetails(selectedSubmission.candidateId);
      setShowApproveConfirm(false);
      setApproveStep(null);
      success(`Step Approved`, "This step has been approved successfully!");
    } catch (err) {
      error("Approval Failed", `${err instanceof Error ? err.message : "Unknown error"}`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleTemplateSelection = async () => {
    if (actionLoading) return;
    if (!selectedSubmission || !selectedTemplate) return;
    try {
      setActionLoading(true);
      await approveStep1GeneralDetails(candidateDetails?.generalDetails.id, adminCredentials.id ?? "", adminCredentials.name ?? "", token!, graphToken, selectedTemplate);
      await fetchSubmissions(searchQuery, filterStatus, filterStep, currentPage);
      await fetchCandidateDetails(selectedSubmission.candidateId);
      setShowTemplateModal(false);
      setSelectedTemplate("");
      setApproveStep(null);
      success("Step 1 Approved", "General details approved and contract generated successfully!");
    } catch (err) {
      error("Approval Failed", `${err instanceof Error ? err.message : "Unknown error"}`);
    } finally {
      setActionLoading(false);
    }
  };

  // Returns the file name of the template matching the candidate's role, or "" when there is none.
  const loadContractTemplates = async (): Promise<string> => {
    if (!token) return "";
    try {
      setLoadingTemplates(true);
      const templates = await getContractTemplates(token);
      setContractTemplates(templates);
      if (candidateDetails?.roleTitle) {
        const match = templates.find((t: any) => t.displayName === candidateDetails.roleTitle || t.fileName === `${candidateDetails.roleTitle}.docx`);
        if (match) {
          setSelectedTemplate(match.fileName);
          return match.fileName;
        }
      }
      return "";
    } catch {
      error("Failed to Load Templates", "Unable to fetch contract templates");
      return "";
    } finally {
      setLoadingTemplates(false);
    }
  };

  const closeReissueModal = () => {
    setShowReissueModal(false);
    setReissueTemplate("");
    setReissueReason("");
    setNotifyCandidateOnReissue(true);
  };

  const handleReissueContract = async () => {
    if (isReissuing) return;
    if (!selectedSubmission || !candidateDetails?.offerId) return;
    if (!graphToken) {
      error("Microsoft Sign-in Required", "A Microsoft Graph token is needed to generate the contract PDF. Please sign in again.");
      return;
    }
    try {
      setIsReissuing(true);
      const result = await reissueContract(
        candidateDetails.offerId,
        adminCredentials.id ?? "",
        adminCredentials.name ?? "",
        token!,
        {
          graphToken,
          selectedTemplateName: reissueTemplate || undefined,
          reason: reissueReason.trim() || undefined,
          notifyCandidate: notifyCandidateOnReissue,
        },
      );
      await fetchSubmissions(searchQuery, filterStatus, filterStep, currentPage);
      await fetchCandidateDetails(selectedSubmission.candidateId);
      closeReissueModal();
      success("Contract Re-issued", result?.message || "A revised contract has been issued to the candidate.");
    } catch (err) {
      error("Re-issue Failed", `${err instanceof Error ? err.message : "Unknown error"}`);
    } finally {
      setIsReissuing(false);
    }
  };

  const handleReject = async () => {
    if (actionLoading) return;
    if (!selectedSubmission || !rejectStep || !rejectReason.trim()) return;
    try {
      setActionLoading(true);
      if (rejectStep === "step1") await rejectStep1GeneralDetails(candidateDetails?.generalDetails.id, adminCredentials.id ?? "", adminCredentials.name ?? "", rejectReason, token!);
      else if (rejectStep === "step2") await rejectStep2SignedContract(candidateDetails?.signedContract?.id, adminCredentials.id ?? "", adminCredentials.name ?? "", rejectReason, token!);
      else if (rejectStep === "step3") rejectStep3WelcomePack(candidateDetails?.welcomePackDetails?.id, adminCredentials.id ?? "", adminCredentials.name ?? "", rejectReason, token!);
      await fetchSubmissions(searchQuery, filterStatus, filterStep, currentPage);
      await fetchCandidateDetails(selectedSubmission.candidateId);
      setShowRejectModal(false);
      setRejectReason("");
      setRejectStep(null);
      success("Step Rejected", "This step has been rejected.");
    } catch (err) {
      error("Rejection Failed", `${err instanceof Error ? err.message : "Unknown error"}`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleDownloadSignedContract = async () => {
    if (actionLoading) return;
    if (!candidateDetails?.offerId) return;
    try {
      setActionLoading(true);
      const blob = await downloadSignedContractByCandidate(candidateDetails.offerId, token!);
      const url = window.URL.createObjectURL(blob);
      const fileName = candidateDetails.generalDetails?.legalName ? `SignedContract-${candidateDetails.generalDetails.legalName}.pdf` : "SignedContract.pdf";
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      success("Download Complete", "Contract downloaded successfully!");
    } catch (err: any) {
      error("Download Failed", `${err?.message || "Failed to download contract"}`);
    } finally {
      setActionLoading(false);
    }
  };

  // Opens the shared PDF modal on a freshly fetched document. Revokes the object URL still held by
  // a previous preview and clears the page count, so the last document's pages aren't rendered
  // against the new file while it loads.
  const openPdfPreview = (blob: Blob, title: string) => {
    if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    setPdfPageCount(0);
    setPdfUrl(URL.createObjectURL(blob));
    setPdfModalTitle(title);
    setShowPdfModal(true);
  };

  const handlePreviewSignedContract = async () => {
    if (previewLoading) return;
    if (!candidateDetails?.offerId) return;
    try {
      setPreviewLoading(true);
      const blob = await downloadSignedContractByCandidate(candidateDetails.offerId, token!);
      openPdfPreview(blob, "Signed Contract Preview");
    } catch (err: any) {
      error("Failed to Load PDF", `${err?.message || "Unable to load signed contract"}`);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleMarkComplete = async () => {
    if (isMarkingComplete) return;
    if (!selectedSubmission?.id || !adminCredentials.id || !adminCredentials.name) return;
    if (!candidateDetails?.msEmail) { setShowMsEmailModal(true); return; }
    try {
      setIsMarkingComplete(true);
      await markOnboardingComplete(selectedSubmission.id, adminCredentials.id, adminCredentials.name, token!, candidateDetails.msEmail);
      success("Onboarding Complete", "Candidate onboarding has been marked as complete!");
      await fetchSubmissions(searchQuery, filterStatus, filterStep, currentPage);
      setSelectedSubmission(null);
      setCandidateDetails(null);
    } catch (err: any) {
      if (err.requiresMsEmail) { setShowMsEmailModal(true); return; }
      error("Failed to Mark Complete", err.message || "An error occurred");
    } finally {
      setIsMarkingComplete(false);
    }
  };

  const handleSubmitMsEmail = async () => {
    if (isMarkingComplete) return;
    if (!msEmail.trim()) { error("Email Required", "Please enter a valid Microsoft email address"); return; }
    if (!selectedSubmission?.id || !adminCredentials.id || !adminCredentials.name) return;
    try {
      setIsMarkingComplete(true);
      await markOnboardingComplete(selectedSubmission.id, adminCredentials.id, adminCredentials.name, token!, msEmail.trim());
      success("Onboarding Complete", "MS email added and onboarding marked as complete!");
      setShowMsEmailModal(false);
      setMsEmail("");
      await fetchSubmissions(searchQuery, filterStatus, filterStep, currentPage);
      setSelectedSubmission(null);
      setCandidateDetails(null);
    } catch (err: any) {
      error("Failed to Mark Complete", err.message || "An error occurred");
    } finally {
      setIsMarkingComplete(false);
    }
  };

  const getStepLabel = (step: string) => {
    const map: Record<string, string> = { offered: "Offered", step1: "General Details", step2: "Contract Signing", step3: "Welcome Pack", completed: "Completed" };
    return map[step] ?? step;
  };

  /* ─── Buddy urgency warning ─── */
  const renderBuddyUrgencyWarning = () => {
    if (selectedSubmission?.currentStep !== "completed" || preAssignedBuddies.length > 0) return null;
    const cd = candidateDetails?.generalDetails?.commencementDate;
    if (!cd) return null;
    const commencementDate = new Date(cd);
    const now = new Date();
    commencementDate.setHours(0, 0, 0, 0);
    now.setHours(0, 0, 0, 0);
    const diffDays = Math.ceil((commencementDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    if (diffDays <= 3) {
      return <p className="text-sm text-red-600 dark:text-red-400 font-semibold">Urgent: Assign a buddy — commencement in {diffDays < 1 ? "less than a day" : `${diffDays} day${diffDays > 1 ? "s" : ""}`}</p>;
    }
    const assignByDate = new Date(commencementDate);
    assignByDate.setDate(assignByDate.getDate() - 3);
    return <p className="text-sm text-yellow-700 dark:text-yellow-400 font-semibold">Assign buddy before <b>{assignByDate.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })}</b></p>;
  };

  /* ─── Step approval/rejection action row ─── */
  const renderStepActions = (step: "step1" | "step2" | "step3", stepData: any) => {
    const isPending = stepData?.status === "pending";
    const isApproved = stepData?.status === "approved";
    const isRejected = stepData?.status === "rejected";

    // Step 2: awaiting candidate submission
    const awaitingSubmit = step === "step2" && stepData?.status === "initiated" && !stepData?.submittedAt;
    const noContract = step === "step2" && !stepData;

    if (!isPending && !isRejected) return null;
    if (isApproved) return null;

    return (
      <div className="flex items-center gap-3 mt-4">
        {awaitingSubmit && (
          <div className="flex items-center gap-2 text-xs text-blue-700 dark:text-blue-300 bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg px-3 py-2 flex-1">
            <ClockIcon className="h-4 w-4 flex-shrink-0" />
            Awaiting candidate to submit their signed contract
          </div>
        )}
        {!awaitingSubmit && !noContract && (
          <>
            <button
              onClick={() => { setRejectStep(step); setShowRejectModal(true); }}
              disabled={actionLoading}
              className="px-4 py-2 rounded-lg border-2 border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 bg-white dark:bg-transparent hover:bg-red-50 dark:hover:bg-red-950/30 font-medium text-sm transition-colors disabled:opacity-50"
            >
              Reject
            </button>
            <button
              onClick={() => { setApproveStep(step); setShowApproveConfirm(true); }}
              disabled={actionLoading}
              className="px-4 py-2 rounded-lg bg-entntblue text-white hover:bg-blue-700 font-medium text-sm transition-colors disabled:opacity-50 flex items-center gap-2"
            >
              <CheckIcon className="h-4 w-4" />
              {step === "step1" ? "Approve & Generate Contract" : "Approve"}
            </button>
          </>
        )}
      </div>
    );
  };

  /* ─── Internship period / part-time summary (only when a date was configured) ─── */
  const renderPartTimeSummary = () => {
    const internshipStart = candidateDetails?.internshipPeriodStartDate;
    if (!internshipStart) return null;

    const interested = candidateDetails?.isInterestedInPartTime;
    const partTimeStart = candidateDetails?.partTimeStartDate;

    return (
      <div className="grid grid-cols-4 gap-3">
        <FieldCard
          label="Internship Period Start (Fixed)"
          value={format(new Date(internshipStart), "MMM dd, yyyy")}
        />
        <FieldCard
          label="Interested in Part-Time"
          value={interested === null || interested === undefined ? "Not answered yet" : interested ? "Yes" : "No"}
        />
        <FieldCard
          label="Part-Time Start Date"
          value={partTimeStart ? format(new Date(partTimeStart), "MMM dd, yyyy") : "—"}
        />
        <FieldCard label="Part-Time Schedule" value={partTimeStart ? "4 hrs/day (pro-rata)" : "—"} />
      </div>
    );
  };

  /* ─── Step 1 content ─── */
  const renderStep1 = () => {
    const data = candidateDetails?.generalDetails;
    if (!data) return (
      <div className="space-y-4">
        {renderPartTimeSummary()}
        <div className="rounded-lg border border-dashed border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-darkbglight p-6 text-center text-sm text-gray-500 dark:text-gray-400">
          General details not submitted yet.
        </div>
      </div>
    );
    return (
      <div className="space-y-4">

        {renderPartTimeSummary()}

        <div className="grid grid-cols-4 gap-3">
          <FieldCard label="Legal Name" value={data.legalName} wide />
          <FieldCard label="Date of Birth" value={data.dateOfBirth ? format(new Date(data.dateOfBirth), "MMM dd, yyyy") : "—"} />
          <FieldCard label="Mobile Number" value={data.mobileNumber} />
          <FieldCard label="Email Address" value={data.emailAddress} wide />
          <FieldCard label="Residential Address" value={data.residentialAddress} wide />
          {data.workAddress && <FieldCard label="Work Address" value={data.workAddress} wide />}
          <FieldCard label="Aadhaar Number" value={data.aadhaarNumber} />
          <FieldCard label="Commencement Date" value={data.commencementDate ? format(new Date(data.commencementDate), "MMM dd, yyyy") : "—"} />
          <FieldCard label="University" value={data.university} />
          <FieldCard label="Department" value={data.collegeDepartment} />
          <FieldCard label="Roll Number" value={data.rollNumber} />
        </div>

        {/* Contract generation failure */}
        {data.status === "approved" && !candidateDetails?.signedContract && (
          <div className="rounded-lg border border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-950/30 p-4">
            <div className="flex items-start gap-3">
              <XCircleIcon className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-red-800 dark:text-red-200 mb-1">Contract generation failed</p>
                <p className="text-xs text-red-700 dark:text-red-300 mb-3">The employment contract PDF was not saved. The candidate cannot download their contract until you re-generate it.</p>
                {regenerateResult && (
                  <p className={`text-xs font-medium mb-2 ${regenerateResult.ok ? "text-green-700 dark:text-green-300" : "text-red-700 dark:text-red-300"}`}>{regenerateResult.msg}</p>
                )}
                <button
                  disabled={regenerating || !graphToken}
                  onClick={async () => {
                    if (!graphToken || !candidateDetails?.offerId) return;
                    setRegenerating(true);
                    setRegenerateResult(null);
                    try {
                      await regenerateContract(candidateDetails.offerId, graphToken, token!);
                      setRegenerateResult({ ok: true, msg: "✓ Contract generated and saved." });
                      if (selectedSubmission?.candidateId) {
                        const updated = await getOfferedCandidateDetails(selectedSubmission.candidateId, token!);
                        setCandidateDetails(updated);
                      }
                      success("Contract Regenerated", "Contract generated successfully!");
                    } catch (err: any) {
                      setRegenerateResult({ ok: false, msg: "✗ " + (err?.message || "Failed to generate contract") });
                      error("Generation Failed", err?.message || "Failed");
                    } finally {
                      setRegenerating(false);
                    }
                  }}
                  className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium text-sm disabled:opacity-50"
                >
                  {regenerating ? <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />Generating...</> : <><ArrowDownTrayIcon className="h-4 w-4" />Re-generate Contract</>}
                </button>
              </div>
            </div>
          </div>
        )}

        {data.status === "rejected" && data.rejectionReason && (
          <div className="rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950/30 p-4">
            <p className="text-xs font-semibold text-red-700 dark:text-red-300 uppercase tracking-wide mb-1">Rejection reason</p>
            <p className="text-sm text-red-800 dark:text-red-200">{data.rejectionReason}</p>
          </div>
        )}

        {renderStepActions("step1", data)}
      </div>
    );
  };

  /* ─── Step 2 content ─── */
  const renderStep2 = () => {
    const data = candidateDetails?.signedContract;
    if (!data) return (
      <div className="rounded-lg border border-dashed border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-darkbglight p-6 text-center text-sm text-gray-500 dark:text-gray-400">
        Contract signing not yet started.
      </div>
    );
    const awaitingSubmit = data.status === "initiated" && !data.submittedAt;
    // A contract can only be replaced while it is unapproved, and only once Step 1 is signed off
    // (the generated PDF is built from the approved general details).
    const canReissueContract =
      data.status !== "approved" && candidateDetails?.generalDetails?.status === "approved";
    return (
      <div className="space-y-4">

        <div className="grid grid-cols-2 gap-4">
          {/* Unsigned contract preview */}
          {(data.status === "initiated" || data.status === "rejected") && (
            <div className="flex flex-col gap-3 p-4 rounded-lg border-2 border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/30">
              <div className="flex items-center gap-3">
                <div className="p-2.5 bg-amber-100 dark:bg-amber-900 rounded-lg">
                  <DocumentTextIcon className="h-8 w-8 text-amber-600 dark:text-amber-300" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-900 dark:text-gray-200">Generated contract (unsigned)</p>
                  {data.generatedContractGeneratedAt && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Generated {format(new Date(data.generatedContractGeneratedAt), "MMM dd, yyyy HH:mm")}</p>
                  )}
                </div>
              </div>
              <button
                onClick={async () => {
                  try {
                    if (selectedSubmission) {
                      const blob = await getGeneratedContractPdf(selectedSubmission.id, token!);
                      openPdfPreview(blob, "Generated Contract Preview");
                    }
                  } catch (err) {
                    error("Failed to Load PDF", err instanceof Error ? err.message : "Unable to load PDF");
                  }
                }}
                className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-white dark:bg-darkbglight border border-amber-300 dark:border-amber-700 text-amber-700 dark:text-amber-300 rounded-md hover:bg-amber-50 text-sm font-medium transition-colors"
              >
                <DocumentTextIcon className="h-4 w-4" />Preview Contract
              </button>
            </div>
          )}

          {/* Signed contract from candidate */}
          {data.submittedAt && (
            <div className="flex flex-col gap-3 p-4 rounded-lg border-2 border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-darkbglight">
              <div className="flex items-center gap-3">
                <div className="p-2.5 bg-blue-100 dark:bg-blue-900 rounded-lg">
                  <DocumentTextIcon className="h-8 w-8 text-blue-600 dark:text-blue-300" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-900 dark:text-gray-200">Signed contract (from candidate)</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{data.signedContractFileName || "Contract.pdf"}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Submitted {format(new Date(data.submittedAt), "MMM dd, yyyy HH:mm")}</p>
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={handlePreviewSignedContract}
                  disabled={previewLoading}
                  className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-white dark:bg-darkbglight border border-blue-300 dark:border-blue-700 text-blue-700 dark:text-blue-300 rounded-md hover:bg-blue-50 dark:hover:bg-blue-950/30 text-sm font-medium transition-colors disabled:opacity-50"
                >
                  {previewLoading
                    ? <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 dark:border-blue-300" />Loading...</>
                    : <><DocumentTextIcon className="h-4 w-4" />Preview</>}
                </button>
                <button onClick={handleDownloadSignedContract} className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-white dark:bg-darkbglight border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-800 text-sm font-medium transition-colors">
                  <ArrowDownTrayIcon className="h-4 w-4" />Download
                </button>
              </div>
            </div>
          )}
        </div>

        {awaitingSubmit && (
          <div className="flex items-center gap-2 text-sm text-blue-700 dark:text-blue-300 bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg px-4 py-3">
            <ClockIcon className="h-4 w-4 flex-shrink-0" />
            Contract sent — awaiting candidate to download, sign, and upload.
          </div>
        )}

        <div className="rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/30 p-4">
          <p className="text-xs font-semibold text-amber-800 dark:text-amber-200 uppercase tracking-wide mb-2">Review checklist</p>
          <div className="grid grid-cols-2 gap-2">
            {["Verify the document is properly signed", "Check all required fields are filled correctly", "Ensure the document is clear and legible", "Confirm signature matches candidate's name"].map((item) => (
              <div key={item} className="flex items-start gap-2 text-sm text-amber-800 dark:text-amber-300">
                <CheckCircleIcon className="h-4 w-4 mt-0.5 flex-shrink-0" />{item}
              </div>
            ))}
          </div>
        </div>

        {data.status === "rejected" && data.rejectionReason && (
          <div className="rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950/30 p-4">
            <p className="text-xs font-semibold text-red-700 dark:text-red-300 uppercase tracking-wide mb-1">Rejection reason</p>
            <p className="text-sm text-red-800 dark:text-red-200">{data.rejectionReason}</p>
          </div>
        )}

        {/* Re-issue: available for as long as the contract has not been approved */}
        {canReissueContract && (
          <div className="rounded-lg border border-purple-200 dark:border-purple-800 bg-purple-50 dark:bg-purple-950/30 p-4">
            <div className="flex items-start gap-3">
              <ArrowPathIcon className="h-5 w-5 text-purple-600 dark:text-purple-300 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-purple-900 dark:text-purple-200 mb-1">Issue a revised contract</p>
                <p className="text-xs text-purple-800 dark:text-purple-300 mb-3">
                  Re-generate this candidate's contract from a different template or with corrected details, send it
                  again, and notify them by email. Step 2 resets so they sign the new copy — any signed document they
                  already uploaded stops counting as a submission.
                </p>
                <button
                  onClick={() => setShowReissueModal(true)}
                  disabled={isReissuing || !graphToken}
                  title={!graphToken ? "A Microsoft Graph token is required to generate the contract" : undefined}
                  className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-medium text-sm disabled:opacity-50"
                >
                  <ArrowPathIcon className="h-4 w-4" />
                  Re-generate &amp; Resend Contract
                </button>
              </div>
            </div>
          </div>
        )}

        {renderStepActions("step2", data)}
      </div>
    );
  };

  /* ─── Step 3 content ─── */
  const renderStep3 = () => {
    const data = candidateDetails?.welcomePackDetails;
    const bank = data?.bankDetails;
    if (!data) return (
      <div className="rounded-lg border border-dashed border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-darkbglight p-6 text-center text-sm text-gray-500 dark:text-gray-400">
        Welcome pack not submitted yet.
      </div>
    );
    return (
      <div className="space-y-4">

        {bank && (
          <>
            <StepDivider label="Bank Account Details" />
            <div className="grid grid-cols-3 gap-3">
              <FieldCard label="Bank Name" value={bank.bankName} />
              <FieldCard label="Branch Name" value={bank.branchName} />
              <FieldCard label="Account Holder" value={bank.accountHolderName} />
              <FieldCard label="Account Number" value={bank.accountNumber} />
              <FieldCard label="IFSC Code" value={bank.ifscCode} />
              <FieldCard label="Account Type" value={bank.accountType} />
            </div>
          </>
        )}

        <div className="flex items-center gap-2 text-sm text-blue-800 dark:text-blue-200 bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg px-4 py-3">
          <CheckCircleIcon className="h-4 w-4 flex-shrink-0" />
          Jurisdiction acknowledged: <span className="font-semibold ml-1">{data.jurisdictionAcknowledged ? "Yes" : "No"}</span>
        </div>

        {renderStepActions("step3", data)}
      </div>
    );
  };

  /* ─── Progress tracker (compact horizontal) ─── */
  const renderProgressBar = () => {
    if (!candidateDetails) return null;
    const steps = [
      { id: "step1" as const, label: "General Details", status: candidateDetails.generalDetails?.status ?? "not_started" },
      { id: "step2" as const, label: "Contract Signing", status: candidateDetails.signedContract?.status ?? "not_started" },
      { id: "step3" as const, label: "Welcome Pack", status: candidateDetails.welcomePackDetails?.status ?? "not_started" },
    ];
    return (
      <div className="flex items-center gap-0 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-darkbglight">
        {steps.map((step, i) => {
          const isApproved = step.status === "approved";
          const isPending = step.status === "pending";
          const isRejected = step.status === "rejected";
          const isActive = activeTab === step.id;
          const isClickable = candidateDetails.generalDetails || candidateDetails.signedContract || candidateDetails.welcomePackDetails;

          return (
            <React.Fragment key={step.id}>
              <button
                onClick={() => isClickable && setActiveTab(step.id)}
                disabled={!isClickable}
                className={`flex-1 flex items-center gap-3 px-6 py-4 transition-all relative ${isActive
                  ? "bg-blue-50 dark:bg-blue-950/30"
                  : "hover:bg-gray-50 dark:hover:bg-darkbg"
                  } ${!isClickable ? "cursor-default" : "cursor-pointer"}`}
              >
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-colors flex-shrink-0 ${isApproved
                  ? "bg-green-500 text-white"
                  : isRejected
                    ? "bg-red-500 text-white"
                    : isPending
                      ? "bg-yellow-400 text-white"
                      : "bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400"
                  }`}>
                  {isApproved ? <CheckIcon className="h-4 w-4" /> : isRejected ? <XMarkIcon className="h-4 w-4" /> : i + 1}
                </div>
                <div className="flex-1 min-w-0 text-left">
                  <p className={`text-sm font-semibold ${isActive ? "text-gray-900 dark:text-gray-100" : "text-gray-600 dark:text-gray-400"}`}>
                    {step.label}
                  </p>
                  <p className={`text-xs font-medium capitalize mt-0.5 ${isApproved ? "text-green-600 dark:text-green-400"
                    : isRejected ? "text-red-500 dark:text-red-400"
                      : isPending ? "text-yellow-600 dark:text-yellow-400"
                        : "text-gray-400 dark:text-gray-500"
                    }`}>
                    {step.status.replace("_", " ")}
                  </p>
                </div>
                {isActive && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-entntblue" />
                )}
              </button>
              {i < steps.length - 1 && (
                <div className="w-px h-12 bg-gray-200 dark:bg-gray-700" />
              )}
            </React.Fragment>
          );
        })}
      </div>
    );
  };

  const filteredSubmissions = submissions.filter((sub) =>
    (filterStatus === "all" || sub.currentStatus === filterStatus) &&
    (filterStep === "all" || sub.currentStep === filterStep)
  );

  /* ─────────────────────── Render ─────────────────────── */
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-darkbg">

      <div className={`flex h-[calc(100vh-73px)] overflow-hidden`}>
        {/* ════════════════ LEFT PANEL — List ════════════════ */}
        <div className={`flex flex-col border-r border-gray-200 dark:border-gray-700 bg-white dark:bg-darkbglight transition-all duration-200 ${selectedSubmission ? "w-[360px] min-w-[320px]" : "flex-1"}`}>

          <div className="border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-darkbglight px-6 py-4">
            <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">Onboard Candidates</h1>
          </div>
          {/* Search & Filters */}
          <div className="px-4 pt-4 pb-3 border-b border-gray-100 dark:border-gray-700 space-y-3">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search name, email, role…"
                className="w-full pl-9 pr-8 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-darkbg text-gray-900 dark:text-gray-300 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-entntblue focus:border-entntblue"
              />
              {searchQuery && (
                <button onClick={() => setSearchQuery("")} className="absolute right-2.5 top-1/2 -translate-y-1/2">
                  <XMarkIcon className="h-4 w-4 text-gray-400 hover:text-gray-600" />
                </button>
              )}
            </div>
            <div className="flex items-center gap-2">
              <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value as any)} className="flex-1 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-darkbg text-gray-900 dark:text-gray-300 text-xs py-1.5 px-2 focus:outline-none focus:ring-1 focus:ring-entntblue">
                <option value="all">All Status</option>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
                <option value="initiated">Initiated</option>
              </select>
              <select value={filterStep} onChange={(e) => setFilterStep(e.target.value as any)} className="flex-1 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-darkbg text-gray-900 dark:text-gray-300 text-xs py-1.5 px-2 focus:outline-none focus:ring-1 focus:ring-entntblue">
                <option value="all">All Steps</option>
                <option value="offered">Offered</option>
                <option value="step1">General Details</option>
                <option value="step2">Contract Signing</option>
                <option value="step3">Welcome Pack</option>
                <option value="completed">Completed</option>
              </select>
              <button onClick={() => { setFilterStatus("all"); setFilterStep("all"); setSearchQuery(""); }} className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 relative" title="Reset filters">
                <FunnelIcon className="h-4 w-4" />
                <ArrowPathIcon className="h-2.5 w-2.5 absolute bottom-0 -right-0.5 bg-white dark:bg-darkbglight rounded-full" />
              </button>
            </div>
            {totalCount > 0 && (
              <p className="text-xs text-gray-400 dark:text-gray-500">{totalCount} candidate{totalCount !== 1 ? "s" : ""}</p>
            )}
          </div>

          {/* List */}
          <div className="flex-1 overflow-y-auto">
            {isLoading ? (
              <div className="flex flex-col items-center justify-center h-40 gap-3">
                <div className="animate-spin rounded-full h-7 w-7 border-b-2 border-entntblue" />
                <p className="text-xs text-gray-400">Loading…</p>
              </div>
            ) : filteredSubmissions.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-40 text-gray-400 text-sm">
                No submissions found
              </div>
            ) : (
              <ul className="divide-y divide-gray-100 dark:divide-gray-700">
                {filteredSubmissions.map((sub) => {
                  const isSelected = selectedSubmission?.id === sub.id;
                  return (
                    <li key={sub.id}>
                      <button
                        onClick={() => sub.currentStep === "offered" ? setInitiateCandidate(sub) : handleReviewButtonClick(sub)}
                        className={`w-full text-left px-4 py-3 transition-colors hover:bg-gray-50 dark:hover:bg-darkbg/60 focus:outline-none ${isSelected ? "bg-blue-50 dark:bg-blue-950/30 border-l-2 border-entntblue" : "border-l-2 border-transparent"}`}
                      >
                        <div className="flex items-start gap-3">
                          <div className="flex-shrink-0 w-8 h-8 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mt-0.5">
                            <UserIcon className="h-4 w-4 text-gray-500 dark:text-gray-300" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between gap-1">
                              <p className="text-sm font-semibold text-gray-900 dark:text-gray-200 truncate">{sub.candidateName}</p>
                              <StatusBadge status={sub.currentStatus} />
                            </div>
                            <p className="text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5">{sub.roleTitle}</p>
                            <div className="flex items-center justify-between">

                              <p className="text-xs text-gray-400 dark:text-gray-500 truncate">
                                {sub.candidateEmail}
                              </p>
                              <p className="flex flex-col">
                                <span className="text-[10px] text-gray-400 dark:text-gray-500 font-medium">
                                  {getStepLabel(sub.currentStep)}
                                </span>
                                <span className="text-[10px] text-gray-400 dark:text-gray-500">
                                  {format(parseUtcDate(sub.updatedAt), "MMM dd, HH:mm")}
                                </span>
                              </p>
                            </div>
                          </div>
                          <ChevronRightIcon className={`h-4 w-4 flex-shrink-0 mt-2 transition-colors ${isSelected ? "text-entntblue" : "text-gray-300 dark:text-gray-600"}`} />
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="border-t border-gray-100 dark:border-gray-700 px-4 py-2.5 flex items-center justify-between">
              <button onClick={() => setCurrentPage(Math.max(1, currentPage - 1))} disabled={currentPage === 1} className="text-xs text-gray-600 dark:text-gray-300 disabled:opacity-40 hover:text-entntblue">← Prev</button>
              <span className="text-xs text-gray-400">{currentPage} / {totalPages}</span>
              <button onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))} disabled={currentPage === totalPages} className="text-xs text-gray-600 dark:text-gray-300 disabled:opacity-40 hover:text-entntblue">Next →</button>
            </div>
          )}
        </div>

        {/* ════════════════ RIGHT PANEL — Details ════════════════ */}
        {selectedSubmission ? (
          <div className="flex-1 flex flex-col overflow-hidden bg-gray-50 dark:bg-darkbg">
            {/* Detail header */}
            <div className="bg-white dark:bg-darkbglight border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex-shrink-0">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100">{selectedSubmission.candidateName}</h2>
                    <StatusBadge status={selectedSubmission.currentStatus} />
                  </div>
                  <div className="flex items-center gap-3 mt-1 text-sm text-gray-500 dark:text-gray-400">
                    <span>{selectedSubmission.roleTitle}</span>
                    <span className="text-gray-300 dark:text-gray-600">·</span>
                    <span className="flex items-center gap-1"><EnvelopeIcon className="h-3.5 w-3.5" />{selectedSubmission.candidateEmail}</span>
                  </div>
                </div>
                <button onClick={() => { setSelectedSubmission(null); setCandidateDetails(null); setPreAssignedBuddies([]); setActiveTab("step1"); }} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 p-1 flex-shrink-0">
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
            </div>

            {isStepDetailsLoading ? (
              <div className="flex-1 flex flex-col items-center justify-center gap-3">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-entntblue" />
                <p className="text-sm text-gray-400">Loading candidate details…</p>
              </div>
            ) : (
              <>
                {/* Tab Navigation */}
                {renderProgressBar()}

                {/* Tab Content Area */}
                <div className="flex-1 overflow-hidden">
                  <div className="h-full overflow-y-auto px-6 py-6">
                    {/* Buddy / completion actions (when Step 3 approved) */}
                    {candidateDetails?.welcomePackDetails?.status === "approved" && (
                      <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-darkbglight p-5 mb-6">
                        <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3">Onboarding Actions</p>
                        {renderBuddyUrgencyWarning()}
                        <div className="flex flex-wrap items-center gap-3 mt-2">
                          {preAssignedBuddies.length === 0 ? (
                            <button onClick={() => { setSelectedCandidate(selectedSubmission); setShowBuddyModal(true); }} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-entntblue text-white font-medium text-sm hover:bg-blue-700 transition-colors">
                              <UserIcon className="h-4 w-4" />Assign Buddy
                            </button>
                          ) : (
                            <>
                              <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 dark:bg-blue-950/30 rounded-lg border border-blue-200 dark:border-blue-800 text-sm">
                                <span className="font-medium text-blue-800 dark:text-blue-200">Buddy:</span>
                                <span className="text-blue-700 dark:text-blue-300">{preAssignedBuddies.map((b, i) => <span key={b.id}>{b.name}{i < preAssignedBuddies.length - 1 && ", "}</span>)}</span>
                              </div>
                              {!meetingDetails && (
                                <button onClick={() => { setIsRescheduling(false); setShowCalendarModal(true); }} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-green-600 text-white font-medium text-sm hover:bg-green-700 transition-colors">
                                  <CalendarIcon className="h-4 w-4" />Send Calendar Invite
                                </button>
                              )}
                            </>
                          )}
                          {candidateDetails?.currentStatus !== "completed" && (
                            <button onClick={handleMarkComplete} disabled={isMarkingComplete} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-green-600 text-white font-medium text-sm hover:bg-green-700 transition-colors disabled:opacity-50">
                              {isMarkingComplete ? <><div className="animate-spin h-4 w-4 rounded-full border-b-2 border-white" />Processing…</> : <><CheckCircleIcon className="h-4 w-4" />Mark Onboarding as Complete</>}
                            </button>
                          )}
                        </div>

                        {/* Meeting Details Display */}
                        {meetingDetails && (
                          <div className="mt-4 p-4 bg-green-50 dark:bg-green-950/30 rounded-lg border border-green-200 dark:border-green-800">
                            <div className="flex items-start justify-between gap-3">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-2">
                                  <CalendarIcon className="h-5 w-5 text-green-600 dark:text-green-400" />
                                  <p className="text-sm font-semibold text-green-900 dark:text-green-200">{meetingDetails.meetingTitle}</p>
                                </div>
                                <div className="space-y-1 text-sm text-green-800 dark:text-green-300">
                                  <p className="flex items-center gap-2">
                                    <ClockIcon className="h-4 w-4" />
                                    <span>{format(parseUtcDate(meetingDetails.meetingStartTime), "MMM dd, yyyy 'at' h:mm a")} - {format(parseUtcDate(meetingDetails.meetingEndTime), "h:mm a")}</span>
                                  </p>
                                </div>
                              </div>
                              <div className="flex gap-2">
                                <button
                                  onClick={() => { setIsRescheduling(true); setShowCalendarModal(true); }}
                                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 text-white font-medium text-sm hover:bg-blue-700 transition-colors whitespace-nowrap"
                                >
                                  <ArrowPathIcon className="h-4 w-4" />
                                  Reschedule
                                </button>
                                <a
                                  href={meetingDetails.meetingLink}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-green-600 text-white font-medium text-sm hover:bg-green-700 transition-colors whitespace-nowrap"
                                >
                                  Join Meeting
                                  <ArrowRightIcon className="h-4 w-4" />
                                </a>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Tab Content */}
                    {activeTab === "step1" && renderStep1()}
                    {activeTab === "step2" && renderStep2()}
                    {activeTab === "step3" && renderStep3()}
                  </div>
                </div>
              </>
            )}
          </div>
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center text-gray-400 dark:text-gray-600 bg-gray-50 dark:bg-darkbg select-none">
            <DocumentTextIcon className="h-12 w-12 mb-3 opacity-30" />
            <p className="text-sm font-medium">Select a candidate to review</p>
            <p className="text-xs mt-1 opacity-60">All onboarding steps will appear here</p>
          </div>
        )}
      </div>

      {/* ════════════ Modals ════════════ */}

      {/* Initiate onboarding */}
      {initiateCandidate && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white dark:bg-darkbg rounded-2xl shadow-2xl max-w-md w-full p-7 border border-transparent dark:border-gray-700 relative">
            <button type="button" onClick={() => { setInitiateCandidate(null); setInternshipStartDate(""); }} disabled={actionLoading} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
              <XMarkIcon className="h-5 w-5" />
            </button>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center">
                <CheckCircleIcon className="h-6 w-6 text-entntblue" />
              </div>
              <div>
                <h3 className="text-base font-bold text-gray-900 dark:text-gray-200">Initiate Onboarding</h3>
                <p className="text-xs text-gray-500 dark:text-gray-400">{initiateCandidate.candidateName}</p>
              </div>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-2">Starting this process will:</p>
            <ul className="text-sm text-gray-600 dark:text-gray-300 space-y-1.5 mb-5 ml-2">
              {["Send a role offer email to the candidate", "Request the candidate to fill out their general details", "Require your review and approval at each step"].map((item) => (
                <li key={item} className="flex items-start gap-2"><ArrowRightIcon className="h-4 w-4 mt-0.5 text-entntblue flex-shrink-0" />{item}</li>
              ))}
            </ul>

            {/* Optional internship period start date */}
            <div className="mb-6">
              <label htmlFor="internshipStartDate" className="block text-sm font-medium text-gray-900 dark:text-gray-200">
                Internship Period Start Date <span className="text-xs font-normal text-gray-500 dark:text-gray-400">(Optional)</span>
              </label>
              <input
                id="internshipStartDate"
                type="date"
                value={internshipStartDate}
                min={new Date().toISOString().substring(0, 10)}
                onChange={(e) => setInternshipStartDate(e.target.value)}
                disabled={actionLoading}
                className="mt-2 block w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-darkbglight text-gray-900 dark:text-gray-200 shadow-sm focus:border-entntblue focus:ring-entntblue text-sm p-2.5"
              />
              {internshipStartDate ? (
                <div className="mt-2 rounded-lg bg-blue-50 dark:bg-blue-900/30 p-3 text-xs text-blue-800 dark:text-blue-200 space-y-1">
                  <p>
                    The candidate's commencement date will be fixed to{" "}
                    <b>{format(new Date(`${internshipStartDate}T00:00:00`), "MMM dd, yyyy")}</b> and shown as read-only — they cannot pick their own.
                  </p>
                  <p>They will also be asked whether they want to join part-time (4 hrs/day) before this date, and will receive an email explaining the option.</p>
                </div>
              ) : (
                <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                  Leave blank to keep the existing flow — the candidate selects their own commencement date and the part-time step is skipped.
                </p>
              )}
              <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                Note: the role must have its Salary, Part-Time Salary and FTE Salary configured before onboarding can be initiated — the contract is generated from all three.
              </p>
            </div>

            <div className="flex justify-end gap-3">
              <button onClick={() => { setInitiateCandidate(null); setInternshipStartDate(""); }} disabled={actionLoading} className="px-4 py-2 rounded-lg text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 font-medium text-sm">Cancel</button>
              <button onClick={handleInitiateOnboarding} disabled={actionLoading} className="px-4 py-2 rounded-lg font-medium text-sm bg-entntblue text-white hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2">
                {actionLoading ? <><div className="animate-spin h-4 w-4 rounded-full border-b-2 border-white" />Initiating…</> : "Confirm & Send"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Approve confirmation */}
      {showApproveConfirm && selectedSubmission && approveStep && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-[60]">
          <div className="bg-white dark:bg-darkbg rounded-xl shadow-2xl max-w-md w-full p-6 border border-transparent dark:border-gray-700">
            <div className="w-12 h-12 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircleIcon className="h-7 w-7 text-green-600" />
            </div>
            <h3 className="text-base font-bold text-gray-900 dark:text-gray-200 text-center mb-2">
              {approveStep === "step1" ? "Approve & Select Contract Template?" : `Approve ${getStepLabel(approveStep)}?`}
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-300 text-center mb-4">
              {approveStep === "step1" ? "You'll select a contract template to generate for this candidate." : approveStep === "step2" ? "This approves the signed contract and moves the candidate to Step 3." : "This approves the welcome pack and completes onboarding."}
            </p>
            <div className="flex gap-3">
              <button onClick={() => { setShowApproveConfirm(false); setApproveStep(null); }} disabled={actionLoading} className="flex-1 px-4 py-2.5 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 font-medium text-sm">Cancel</button>
              <button onClick={handleApprove} disabled={actionLoading} className="flex-1 px-4 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium text-sm disabled:opacity-50">
                {actionLoading ? "Processing…" : approveStep === "step1" ? "Continue" : "Confirm"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject */}
      {showRejectModal && selectedSubmission && rejectStep && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-[60]">
          <div className="bg-white dark:bg-darkbg rounded-xl shadow-2xl max-w-md w-full p-6 border border-transparent dark:border-gray-700">
            <div className="w-12 h-12 bg-red-100 dark:bg-red-900 rounded-full flex items-center justify-center mx-auto mb-4">
              <XCircleIcon className="h-7 w-7 text-red-600" />
            </div>
            <h3 className="text-base font-bold text-gray-900 dark:text-gray-200 text-center mb-2">Reject {getStepLabel(rejectStep)}</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 text-center mb-4">Provide a clear reason — this will be sent to the candidate.</p>
            <textarea rows={4} value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} placeholder="Enter rejection reason…" className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-darkbglight text-gray-900 dark:text-gray-300 placeholder-gray-400 shadow-sm focus:border-red-500 focus:ring-red-500 text-sm mb-4 p-3" />
            <div className="flex gap-3">
              <button onClick={() => { setShowRejectModal(false); setRejectReason(""); setRejectStep(null); }} disabled={actionLoading} className="flex-1 px-4 py-2.5 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 font-medium text-sm">Cancel</button>
              <button onClick={handleReject} disabled={!rejectReason.trim() || actionLoading} className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium text-sm disabled:opacity-50">
                {actionLoading ? "Rejecting…" : "Confirm Rejection"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Template Selection */}
      {showTemplateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-[70]">
          <div className="bg-white dark:bg-darkbg rounded-xl shadow-2xl max-w-2xl w-full p-6 border border-transparent dark:border-gray-700 max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <DocumentTextIcon className="h-6 w-6 text-entntblue" />
                <h3 className="text-base font-bold text-gray-900 dark:text-gray-200">Select Contract Template</h3>
              </div>
              <button onClick={() => { setShowTemplateModal(false); setSelectedTemplate(""); setActionLoading(false); }} disabled={actionLoading} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">Choose the contract template to generate for this candidate.</p>
            {loadingTemplates ? (
              <div className="flex items-center justify-center py-10 gap-3">
                <div className="animate-spin h-7 w-7 rounded-full border-b-2 border-entntblue" />
                <span className="text-sm text-gray-500">Loading templates…</span>
              </div>
            ) : (
              <div className="flex-1 overflow-y-auto space-y-2 mb-5">
                {contractTemplates.map((t) => (
                  <label key={t.fileName} className={`flex items-center gap-3 p-3.5 rounded-lg border-2 cursor-pointer transition-all ${selectedTemplate === t.fileName ? "border-entntblue bg-blue-50 dark:bg-blue-950/30" : "border-gray-200 dark:border-gray-700 hover:border-gray-300"}`}>
                    <input type="radio" name="template" value={t.fileName} checked={selectedTemplate === t.fileName} onChange={(e) => setSelectedTemplate(e.target.value)} className="w-4 h-4 text-entntblue focus:ring-entntblue" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <DocumentTextIcon className="h-4 w-4 text-gray-400" />
                        <span className="text-sm font-medium text-gray-900 dark:text-gray-200">{t.displayName}</span>
                        {candidateDetails?.roleTitle === t.displayName && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 font-semibold">Recommended</span>
                        )}
                      </div>
                      <p className="text-xs text-gray-400 mt-0.5">{t.fileName} · {(t.size / 1024).toFixed(1)} KB</p>
                    </div>
                  </label>
                ))}
              </div>
            )}
            <div className="flex gap-3 border-t border-gray-100 dark:border-gray-700 pt-4">
              <button onClick={() => { setShowTemplateModal(false); setSelectedTemplate(""); setActionLoading(false); }} disabled={actionLoading} className="flex-1 px-4 py-2.5 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 font-medium text-sm">Cancel</button>
              <button onClick={loadContractTemplates} disabled={actionLoading} className="px-4 py-2.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 font-medium text-sm">
                <ArrowPathIcon className="h-4 w-4 inline mr-1" />Refresh
              </button>
              <button onClick={handleTemplateSelection} disabled={!selectedTemplate || actionLoading} className="flex-1 px-4 py-2.5 bg-entntblue text-white rounded-lg hover:bg-blue-700 font-medium text-sm disabled:opacity-50 flex items-center justify-center gap-2">
                {actionLoading ? <><div className="animate-spin h-4 w-4 rounded-full border-b-2 border-white" />Generating…</> : <><CheckIcon className="h-4 w-4" />Approve & Generate</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Re-issue Contract */}
      {showReissueModal && candidateDetails && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-[70]">
          <div className="bg-white dark:bg-darkbg rounded-xl shadow-2xl max-w-2xl w-full p-6 border border-transparent dark:border-gray-700 max-h-[85vh] flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <ArrowPathIcon className="h-6 w-6 text-purple-600" />
                <h3 className="text-base font-bold text-gray-900 dark:text-gray-200">Re-generate &amp; Resend Contract</h3>
              </div>
              <button onClick={closeReissueModal} disabled={isReissuing} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
              Pick the contract to issue to <span className="font-medium text-gray-700 dark:text-gray-300">{candidateDetails.name || selectedSubmission?.candidateName}</span>.
              The new copy replaces the one they were sent earlier and Step 2 restarts.
            </p>

            {loadingTemplates ? (
              <div className="flex items-center justify-center py-10 gap-3">
                <div className="animate-spin h-7 w-7 rounded-full border-b-2 border-purple-600" />
                <span className="text-sm text-gray-500">Loading templates…</span>
              </div>
            ) : (
              <div className="flex-1 overflow-y-auto space-y-2 mb-4 min-h-0">
                {contractTemplates.map((t) => (
                  <label key={t.fileName} className={`flex items-center gap-3 p-3.5 rounded-lg border-2 cursor-pointer transition-all ${reissueTemplate === t.fileName ? "border-purple-500 bg-purple-50 dark:bg-purple-950/30" : "border-gray-200 dark:border-gray-700 hover:border-gray-300"}`}>
                    <input type="radio" name="reissue-template" value={t.fileName} checked={reissueTemplate === t.fileName} onChange={(e) => setReissueTemplate(e.target.value)} className="w-4 h-4 text-purple-600 focus:ring-purple-500" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <DocumentTextIcon className="h-4 w-4 text-gray-400" />
                        <span className="text-sm font-medium text-gray-900 dark:text-gray-200">{t.displayName}</span>
                        {candidateDetails?.roleTitle === t.displayName && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 font-semibold">Current role</span>
                        )}
                      </div>
                      <p className="text-xs text-gray-400 mt-0.5">{t.fileName} · {(t.size / 1024).toFixed(1)} KB</p>
                    </div>
                  </label>
                ))}
              </div>
            )}

            <div className="mb-3">
              <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                What changed? <span className="font-normal text-gray-400">(optional — included in the candidate's email)</span>
              </label>
              <textarea
                rows={3}
                value={reissueReason}
                onChange={(e) => setReissueReason(e.target.value)}
                placeholder="e.g. Corrected the commencement date and switched to the part-time contract."
                className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-darkbglight text-gray-900 dark:text-gray-300 placeholder-gray-400 shadow-sm focus:border-purple-500 focus:ring-purple-500 text-sm p-3"
              />
            </div>

            <label className="flex items-center gap-2 mb-4 text-sm text-gray-700 dark:text-gray-300">
              <input
                type="checkbox"
                checked={notifyCandidateOnReissue}
                onChange={(e) => setNotifyCandidateOnReissue(e.target.checked)}
                className="w-4 h-4 rounded text-purple-600 focus:ring-purple-500"
              />
              Email the candidate about the revised contract
            </label>

            <div className="flex gap-3 border-t border-gray-100 dark:border-gray-700 pt-4">
              <button onClick={closeReissueModal} disabled={isReissuing} className="flex-1 px-4 py-2.5 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 font-medium text-sm">Cancel</button>
              <button onClick={handleReissueContract} disabled={!reissueTemplate || isReissuing} className="flex-1 px-4 py-2.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-medium text-sm disabled:opacity-50 flex items-center justify-center gap-2">
                {isReissuing ? <><div className="animate-spin h-4 w-4 rounded-full border-b-2 border-white" />Re-issuing…</> : <><ArrowPathIcon className="h-4 w-4" />Re-issue Contract</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MS Email */}
      {showMsEmailModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-[70]">
          <div className="bg-white dark:bg-darkbg rounded-xl shadow-2xl max-w-md w-full p-6 border border-transparent dark:border-gray-700">
            <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center mx-auto mb-4">
              <EnvelopeIcon className="h-7 w-7 text-blue-600 dark:text-blue-300" />
            </div>
            <h3 className="text-base font-bold text-gray-900 dark:text-gray-200 text-center mb-2">Microsoft Email Required</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 text-center mb-4">Enter the employee's Microsoft email to complete the onboarding process.</p>
            <div className="mb-4">
              <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">Employee MS Email</label>
              <input type="email" value={msEmail} onChange={(e) => setMsEmail(e.target.value)} placeholder="employee@company.com" disabled={isMarkingComplete} className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-darkbglight text-gray-900 dark:text-gray-300 placeholder-gray-400 text-sm px-4 py-2.5 focus:outline-none focus:ring-1 focus:ring-blue-500" />
            </div>
            <div className="flex gap-3">
              <button onClick={() => { setShowMsEmailModal(false); setMsEmail(""); setIsMarkingComplete(false); }} disabled={isMarkingComplete} className="flex-1 px-4 py-2.5 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 font-medium text-sm">Cancel</button>
              <button onClick={handleSubmitMsEmail} disabled={!msEmail.trim() || isMarkingComplete} className="flex-1 px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium text-sm disabled:opacity-50 flex items-center justify-center gap-2">
                {isMarkingComplete ? <><div className="animate-spin h-4 w-4 rounded-full border-b-2 border-white" />Processing…</> : "Submit & Complete"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* PDF Preview */}
      {showPdfModal && pdfUrl && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-[70] overflow-y-auto">
          <div className="bg-white dark:bg-darkbg rounded-xl shadow-2xl max-w-4xl w-full my-8 border border-transparent dark:border-gray-700">
            <div className="sticky top-0 bg-white dark:bg-darkbglight border-b border-gray-200 dark:border-gray-700 px-6 py-4 rounded-t-xl flex items-center justify-between z-10">
              <h3 className="text-base font-bold text-gray-900 dark:text-gray-200">{pdfModalTitle}</h3>
              <button onClick={() => { setShowPdfModal(false); if (pdfUrl) { URL.revokeObjectURL(pdfUrl); setPdfUrl(null); } }} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
            <div className="px-6 py-6 max-h-[70vh] overflow-y-auto bg-gray-50 dark:bg-darkbg">
              <div className="flex justify-center">
                <Document file={pdfUrl} onLoadSuccess={({ numPages }) => setPdfPageCount(numPages)} loading={<div className="flex items-center justify-center py-8"><div className="animate-spin h-7 w-7 rounded-full border-b-2 border-entntblue" /></div>} error={<p className="text-red-600 text-center py-8">Failed to load PDF.</p>}>
                  {Array.from(new Array(pdfPageCount), (_, i) => (
                    <div key={`page_${i + 1}`} className="mb-4">
                      <Page pageNumber={i + 1} scale={1.2} renderTextLayer renderAnnotationLayer width={Math.min(700, window.innerWidth - 80)} />
                    </div>
                  ))}
                </Document>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Buddy Modal */}
      {showBuddyModal && selectedCandidate && (
        <OnboardingBuddyModal
          isOpen={showBuddyModal}
          offerId={selectedCandidate.id}
          candidateName={selectedCandidate.candidateName}
          candidateEmail={selectedCandidate.candidateEmail}
          commencementDate={new Date(candidateDetails?.generalDetails.commencementDate)}
          onClose={() => setShowBuddyModal(false)}
          onSuccess={() => { fetchAssignedBuddies(); success("Buddy Assigned", "Buddy assigned successfully!"); }}
        />
      )}

      {/* Calendar Modal */}
      {showCalendarModal && selectedSubmission && candidateDetails && (
        <CalendarInviteModal
          isOpen={showCalendarModal}
          offerId={selectedSubmission.id}
          candidateName={selectedSubmission.candidateName}
          candidateEmail={selectedSubmission.candidateEmail}
          buddies={preAssignedBuddies}
          commencementDate={candidateDetails?.generalDetails?.commencementDate ? new Date(candidateDetails.generalDetails.commencementDate) : undefined}
          existingMeetingDetails={isRescheduling && meetingDetails ? meetingDetails : undefined}
          isReschedule={isRescheduling}
          onClose={() => { setShowCalendarModal(false); setIsRescheduling(false); }}
          onSuccess={() => {
            fetchAssignedBuddies();
            setShowCalendarModal(false);
            setIsRescheduling(false);
            success("Calendar Invite Sent", "Meeting invite sent to candidate and buddies!");
          }}
        />
      )}
    </div>
  );
};